<script lang="ts" setup>
import { ref, reactive, onMounted, watch, onBeforeUnmount } from 'vue';
import { PCheckbox } from '@profeat/ui-kit';
import { onClickOutside, onKeyStroke, watchDebounced } from '@vueuse/core';
import { isEqual } from 'lodash';

import { customerConstants } from '~/components/customers/helper';
import { useContacts } from '~/stores/contacts.store';
import { TagField } from '~/entities/contacts';
import { FilterBuilder } from '~/components/customers/filter-builder';

interface TAG {
  label: string;
  ref: boolean;
  id: string;
}

const { DEFAULT_FILTER_DEBOUNCE } = customerConstants;
const DEFAULT_VALUES: TAG[] = [];
const allTagToggle = ref(false);
const noTagToggle = ref(false);

const contactsStore = useContacts();

const props = defineProps<{ field: TagField; filterBuilder: FilterBuilder; refWrap: HTMLElement }>();
const emit = defineEmits<{
  (event: 'applyFilter', visible?: boolean, withoutCall?: boolean): void;
  (event: 'resetFilter', withoutCall?: boolean): void;
  (event: 'clickOutside'): void;
  (event: 'update:changed', val: boolean): void;
}>();

const filterRef = ref(null);
const isMountUpdate = ref(false);
const tags: TAG[] = reactive([]);
const isUnmounted = ref(false);

onBeforeUnmount(() => {
  isUnmounted.value = true;
});

let tagsInit = JSON.parse(JSON.stringify(tags));

const setToFilter = () => {
  if (isEqual(tags, DEFAULT_VALUES)) return;
  if (isMountUpdate.value) return;

  props.filterBuilder.tag(
    props.field.id,
    tags.filter(({ ref }) => ref).map(({ id }) => id),
  );

  emit('applyFilter');
};
const setDefineFilter = (model: boolean, filter: 'allTags' | 'noTags') => {
  if (model) {
    isMountUpdate.value = false;
    let value: string[] = [];

    if (filter == 'allTags') {
      allTagToggle.value = true;
      noTagToggle.value = false;
      value = tags.map(({ id }) => id);
      tags.forEach((tag) => (tag.ref = true));
    }
    if (filter == 'noTags') {
      noTagToggle.value = true;
      allTagToggle.value = false;
      value = [];
      tags.forEach((tag) => (tag.ref = false));
    }

    props.filterBuilder.tag(props.field.id, value);
    emit('applyFilter');
  } else emit('resetFilter');
};

onClickOutside(() => props.refWrap, () => {
  setToFilter();
  emit('clickOutside');
});
watchDebounced(
  tags,
  () => {
    if (isEqual(tags, DEFAULT_VALUES) || isUnmounted.value) return;

    setToFilter();
  },
  { debounce: DEFAULT_FILTER_DEBOUNCE * 2, deep: true },
);
onKeyStroke(['Enter'], () => {
  setToFilter();
});
onMounted(() => {
  DEFAULT_VALUES.push(
    ...contactsStore.getTags.map(({ name, id }) => ({
      id,
      label: name,
      ref: false,
    })),
  );

  tags.push(...reactive(JSON.parse(JSON.stringify(DEFAULT_VALUES))));
  tagsInit = JSON.parse(JSON.stringify(tags));

  const filters = props.filterBuilder.getFilterById(props.field.id);

  if (Array.isArray(filters)) {
    isMountUpdate.value = true;

    tags.forEach((tag) => filters.includes(tag.id) && (tag.ref = true));
    tagsInit = JSON.parse(JSON.stringify(tags));

    if (filters.length === 0) {
      noTagToggle.value = true;
    } else if (filters.length === tags.length) {
      allTagToggle.value = true;
    }
  }
});

watch(
  tags,
  (val) => {
    const changed = JSON.stringify(tagsInit) !== JSON.stringify(val);
    emit('update:changed', changed);
  },
  { deep: true },
);
</script>

<template>
  <div class="tag-filter" ref="filterRef">
    <div class="values">
      <PCheckbox
        v-model="allTagToggle"
        @update:model-value="(value: boolean) => setDefineFilter(value, 'allTags')"
        :label="$t('customers.allTags')"
        :disabled="!tags.length"
        label-position="right"
        text-size="sm"
      />
      <PCheckbox
        v-model="noTagToggle"
        @update:model-value="(value: boolean) => setDefineFilter(value, 'noTags')"
        :label="$t('customers.noTags')"
        :disabled="!tags.length"
        label-position="right"
        text-size="sm"
      />

      <div v-for="tag in tags" :key="tag.label">
        <PCheckbox
          v-model="tag.ref"
          @update:model-value="() => (isMountUpdate = false)"
          :label="tag.label"
          label-position="right"
          text-size="sm"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tag-filter {
  .values {
    display: flex;
    flex-direction: column;
    grid-gap: 10px;
    max-height: 330px;
    overflow-y: auto;

    :deep(label) {
      .label {
        overflow: hidden;
        max-width: 100px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 25px;
      background: var(--common-rain, #d0dbe4);
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }
}
</style>
